// Plan 24b Phase B, Task 1.6 — End-of-Round Summary panel (Screen 18)
//
// Centered panel showing round number, last 20 event log entries, standings
// snapshot using ScoreboardRows, a CONTINUE button, and a 15-second GSAP
// auto-continue timer.

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { GameEvent } from '@shared/game/types';
import type { GsapTimelineHandle } from '@client/renderer/vfx/gsap-registry';
import type { ScoreboardRowHandle } from '@client/ui/components/scoreboard-row';
import { createScoreboardRow } from '@client/ui/components/scoreboard-row';
import { createButton } from '@client/ui/components/button';
import { SHAPE_GLYPH_FOR_COLOR, colorIdFromIndex } from './shape-palette';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type RoundSummaryPayload = BoardMountPayload & {
  roundNumber: number;
  readyCount: number;
  totalCount: number;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg = #1E2332 */
const PANEL_BG = '#1E2332';

/** color.text.primary = #FFFFFF */
const COLOR_TEXT_PRIMARY = '#FFFFFF';

/** color.text.secondary = #B0B8C8 */
const COLOR_TEXT_SECONDARY = '#B0B8C8';

/** color.text.danger = #E04040 */
const COLOR_TEXT_DANGER = '#E04040';

/** color.hud.trophy-gold = #FFD700 */
const COLOR_TROPHY_GOLD = '#FFD700';

/** color.tile.soul = #40B0C0 */
const COLOR_TILE_SOUL = '#40B0C0';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** shadow.panel = 0 4px 12px rgba(0,0,0,0.50) */
const SHADOW_PANEL = '0 4px 12px rgba(0,0,0,0.50)';

/** typography.size.h3 = 28px */
const H3_SIZE = '28px';

/** typography.size.body = 16px */
const BODY_SIZE = '16px';

/** typography.size.caption = 14px */
const CAPTION_SIZE = '14px';

/** typography.family.headline = 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.family.body = 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif */
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";

/** layout.panel-padding-lg = '24px 28px' */
const PANEL_PADDING_LG = '24px 28px';

/** spacing.sm = 8px */
const SPACING_SM = '8px';

/** Max HP spec constant */
const MAX_HP = 30;

/** Auto-continue duration in seconds */
const AUTO_CONTINUE_SECONDS = 15;

/** Max events to show (last N) */
const MAX_EVENTS = 20;

// ─────────────────────────────────────────────────────────────────────────────
// Player color palette (index = Player.color % 10)
// ─────────────────────────────────────────────────────────────────────────────

const PLAYER_COLORS = [
  '#E04040', // 0 red
  '#4080D0', // 1 blue
  '#40B040', // 2 green
  '#E8D020', // 3 yellow
  '#E08820', // 4 orange
  '#9040C0', // 5 purple
  '#E06090', // 6 pink
  '#40B0C0', // 7 cyan
  '#E8E8E8', // 8 white
  '#404040', // 9 black
];

// ─────────────────────────────────────────────────────────────────────────────
// Event text helper
// ─────────────────────────────────────────────────────────────────────────────

interface EventDisplay {
  text: string;
  color: string;
}

function eventToText(
  event: GameEvent,
  players: Map<string, { name: string; color: number }>,
): EventDisplay {
  const getPlayerName = (id: string | null): string => {
    if (id == null) return 'System';
    return players.get(id)?.name ?? id;
  };

  switch (event.type) {
    case 'economy_change': {
      const name = getPlayerName(event.playerId);
      if (event.resource === 'trophies' && event.newValue > event.oldValue) {
        return { text: `${name} bought a Trophy`, color: COLOR_TROPHY_GOLD };
      }
      if (event.resource === 'keys') {
        if (event.newValue > event.oldValue) {
          return {
            text: `${name} gained ${event.newValue - event.oldValue} keys`,
            color: COLOR_TEXT_SECONDARY,
          };
        }
        return {
          text: `${name} lost ${event.oldValue - event.newValue} keys`,
          color: COLOR_TEXT_SECONDARY,
        };
      }
      return {
        text: `${name} gained ${event.newValue - event.oldValue} trophies`,
        color: COLOR_TEXT_SECONDARY,
      };
    }

    case 'damage': {
      const name = getPlayerName(event.playerId);
      return { text: `${name} took ${event.amount} damage`, color: COLOR_TEXT_DANGER };
    }

    case 'death': {
      const name = getPlayerName(event.playerId);
      return { text: `${name} was eliminated!`, color: COLOR_TEXT_DANGER };
    }

    case 'soul_anchor_claim': {
      const name = getPlayerName(event.playerId);
      return { text: `${name} claimed a Soul Anchor`, color: COLOR_TILE_SOUL };
    }

    default: {
      return { text: `[EVENT: ${event.type}]`, color: COLOR_TEXT_SECONDARY };
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createRoundSummaryScreen(
  ctx: ScreenMountContext<RoundSummaryPayload>,
): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { state, roundNumber, readyCount, totalCount } = payload;

  let disposed = false;

  const rowHandles: ScoreboardRowHandle[] = [];

  // ── Root overlay (fixed full-screen backdrop) ──────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-round-summary';
  root.setAttribute('data-screen-id', 'round-summary');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 160',
    'pointer-events: auto',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'background: rgba(0, 0, 0, 0.55)',
  ].join('; ');

  // ── Centered panel ─────────────────────────────────────────────────────────

  const panel = document.createElement('div');
  panel.className = 'round-summary-panel';
  panel.style.cssText = [
    'width: 640px',
    'max-width: calc(100vw - 32px)',
    `background: ${PANEL_BG}`,
    `border-radius: ${RADIUS_LG}`,
    `box-shadow: ${SHADOW_PANEL}`,
    `padding: ${PANEL_PADDING_LG}`,
    'display: flex',
    'flex-direction: column',
    `gap: ${SPACING_SM}`,
  ].join('; ');

  // ── Title ──────────────────────────────────────────────────────────────────

  const title = document.createElement('h3');
  title.className = 'round-summary-title';
  title.textContent = `Round ${roundNumber} Summary`;
  title.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H3_SIZE}`,
    `color: ${COLOR_TEXT_PRIMARY}`,
    'margin: 0 0 8px',
    'text-align: center',
    'font-weight: 700',
    'line-height: 1',
  ].join('; ');

  panel.appendChild(title);

  // ── Event log ─────────────────────────────────────────────────────────────

  const eventLogContainer = document.createElement('div');
  eventLogContainer.className = 'round-summary-event-log';
  eventLogContainer.style.cssText = [
    'display: flex',
    'flex-direction: column',
    `gap: ${SPACING_SM}`,
    'margin-bottom: 8px',
  ].join('; ');

  // Take the LAST 20 events
  const eventsToShow = state.eventLog.slice(-MAX_EVENTS);

  for (const event of eventsToShow) {
    const display = eventToText(event, state.players);

    const row = document.createElement('div');
    row.className = 'round-summary-event-row';
    row.style.cssText = ['display: flex', 'align-items: center', 'gap: 8px'].join('; ');

    // Colored dot
    const dot = document.createElement('span');
    dot.className = 'round-summary-event-dot';

    // Determine dot color: use player's color if event has a playerId
    let dotColor = '#808080'; // gray for system events
    let playerColorIndex: number | null = null;
    if (event.playerId != null) {
      const player = state.players.get(event.playerId);
      if (player != null) {
        playerColorIndex = player.color % 10;
        dotColor = PLAYER_COLORS[playerColorIndex] ?? PLAYER_COLORS[0];
      }
    }

    dot.style.cssText = [
      'width: 10px',
      'height: 10px',
      'border-radius: 50%',
      `background: ${dotColor}`,
      'flex-shrink: 0',
    ].join('; ');

    // Shape glyph for color-blind accessibility (only for player events)
    const glyphSpan = document.createElement('span');
    glyphSpan.className = 'round-summary-shape-glyph';
    if (playerColorIndex !== null) {
      const colorId = colorIdFromIndex(playerColorIndex);
      glyphSpan.textContent = (colorId ? SHAPE_GLYPH_FOR_COLOR[colorId] : null) ?? '●';
    } else {
      glyphSpan.textContent = '';
    }
    glyphSpan.style.cssText = [
      'font-size: 12px',
      `color: ${dotColor}`,
      'flex-shrink: 0',
      'user-select: none',
    ].join('; ');

    // Event text
    const text = document.createElement('span');
    text.className = 'round-summary-event-text';
    text.textContent = display.text;
    text.style.cssText = [
      `font-family: ${BODY_FAMILY}`,
      `font-size: ${BODY_SIZE}`,
      `color: ${display.color}`,
    ].join('; ');

    row.appendChild(dot);
    row.appendChild(glyphSpan);
    row.appendChild(text);
    eventLogContainer.appendChild(row);
  }

  panel.appendChild(eventLogContainer);

  // ── Standings snapshot ─────────────────────────────────────────────────────

  const standingsTitle = document.createElement('div');
  standingsTitle.className = 'round-summary-standings-title';
  standingsTitle.textContent = 'Standings';
  standingsTitle.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'text-transform: uppercase',
    'letter-spacing: 0.08em',
    'margin-bottom: 4px',
  ].join('; ');

  panel.appendChild(standingsTitle);

  const rowsContainer = document.createElement('div');
  rowsContainer.className = 'round-summary-rows';
  rowsContainer.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 4px'].join('; ');

  // Sort by trophies descending, keys as tiebreaker
  const sorted = [...state.turnOrder]
    .map((id) => ({ id, player: state.players.get(id)! }))
    .filter(({ player }) => player != null)
    .sort((a, b) => {
      const trophyDiff = b.player.trophies - a.player.trophies;
      if (trophyDiff !== 0) return trophyDiff;
      return b.player.keys - a.player.keys;
    });

  for (let i = 0; i < sorted.length; i++) {
    const { id, player } = sorted[i];
    const rank = i + 1;
    const playerColor = PLAYER_COLORS[player.color % 10] ?? PLAYER_COLORS[0];

    const rowMount = document.createElement('div');
    rowMount.className = 'round-summary-row-mount';

    const rowHandle = createScoreboardRow({
      gsapRegistry,
      mount: rowMount,
      playerName: player.name,
      playerColor,
      currentHP: player.hp,
      maxHP: MAX_HP,
      keys: player.keys,
      trophies: player.trophies,
      rank,
    });

    rowHandles.push(rowHandle);

    if (id === payload.localPlayerId) {
      rowHandle.element.style.background = 'rgba(255,255,255,0.06)';
    }

    rowsContainer.appendChild(rowMount);
  }

  panel.appendChild(rowsContainer);

  // ── Continue button ────────────────────────────────────────────────────────

  const continueBtn = createButton({
    gsapRegistry,
    variant: 'secondary',
    label: 'CONTINUE',
    onClick: () => {
      if (disposed) return;
      router.navigate('board-hud', payload);
    },
  });
  continueBtn.element.style.width = '100%';

  panel.appendChild(continueBtn.element);

  // ── Progress indicator ─────────────────────────────────────────────────────

  const progressIndicator = document.createElement('div');
  progressIndicator.className = 'round-summary-progress';
  progressIndicator.textContent = `${readyCount}/${totalCount} ready`;
  progressIndicator.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'text-align: center',
  ].join('; ');

  panel.appendChild(progressIndicator);

  root.appendChild(panel);
  host.appendChild(root);

  // ── Auto-continue (15s GSAP timer) ────────────────────────────────────────

  const autoHandle: GsapTimelineHandle = gsapRegistry.create('round-summary:auto-continue');
  autoHandle.timeline.call(
    () => {
      if (disposed) return;
      router.navigate('board-hud', payload);
    },
    undefined,
    AUTO_CONTINUE_SECONDS,
  );

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill GSAP auto-continue timer
    autoHandle.dispose();

    // Dispose continue button
    continueBtn.dispose();

    // Dispose each row handle
    for (const rowHandle of rowHandles) {
      rowHandle.dispose();
    }

    // Remove from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
